import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchTop10 } from "@/lib/api";
import { useLocale } from "@/lib/locale";
import { Star, Trophy, Tv, Clapperboard, Loader2 } from "lucide-react";

const COUNTRY_NAMES = {
  US: "United States", GB: "United Kingdom", TR: "Türkiye", DE: "Germany",
  FR: "France", JP: "Japan", BR: "Brazil", IN: "India", ES: "Spain", MX: "Mexico",
  IT: "Italy", NL: "Netherlands", AU: "Australia", CA: "Canada",
};

function Top10Column({ icon: Icon, label, items, accent }) {
  return (
    <div className="border border-white/10 rounded-md bg-obsidian-surface/60 overflow-hidden" data-testid={`top10-${label.toLowerCase()}`}>
      <div className={`px-5 py-3 flex items-center gap-2 border-b border-white/10 ${accent}`}>
        <Icon className="h-4 w-4" />
        <span className="font-heading text-sm uppercase tracking-[0.22em]">Top 10 {label}</span>
      </div>
      <ol className="divide-y divide-white/5">
        {items.length === 0 && (
          <li className="px-5 py-8 text-center text-zinc-500 font-body italic text-sm">No data for this region.</li>
        )}
        {items.map((m, idx) => (
          <li key={m.id} className="flex items-center gap-3 px-4 py-3 hover:bg-white/[0.02]" data-testid={`top10-item-${label.toLowerCase()}-${idx}`}>
            <span className={`font-display text-3xl ${idx < 3 ? "text-amber" : "text-zinc-600"} w-10`}>{idx + 1}</span>
            <Link to={`/title/${m.id}`} className="shrink-0">
              <img src={m.poster || "https://images.unsplash.com/photo-1616530940355-351fabd9524b"}
                   alt={m.title}
                   className="h-16 w-12 rounded object-cover border border-white/10" />
            </Link>
            <div className="flex-1 min-w-0">
              <Link to={`/title/${m.id}`} className="font-display text-base text-white hover:text-amber line-clamp-1 transition">
                {m.title}
              </Link>
              <div className="text-xs text-zinc-500 font-heading flex items-center gap-2">
                <span>{m.year}</span>
                {m.rating > 0 && (
                  <span className="inline-flex items-center gap-0.5 text-amber">
                    <Star className="h-3 w-3 fill-amber" /> {m.rating}
                  </span>
                )}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function Top10Section() {
  const { country } = useLocale();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchTop10(country).then(setData).finally(() => setLoading(false));
  }, [country]);

  const countryName = COUNTRY_NAMES[country] || country;

  return (
    <section className="max-w-7xl mx-auto px-6 py-16" data-testid="top10-section">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-amber font-heading mb-2 flex items-center gap-1.5">
            <Trophy className="h-3 w-3" /> Top 10 in {countryName}
          </div>
          <h2 className="font-display text-3xl sm:text-4xl text-white">
            What's hot for you, locally
          </h2>
        </div>
      </div>
      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber" /></div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          <Top10Column
            icon={Tv}
            label="Streaming"
            items={data?.streaming || []}
            accent="text-amber bg-amber/5"
          />
          <Top10Column
            icon={Clapperboard}
            label="Cinemas"
            items={data?.cinemas || []}
            accent="text-red-300 bg-red-500/10"
          />
        </div>
      )}
    </section>
  );
}
